import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  SchedulingConfig,
  SchedulingService as SchedulingServiceModel,
  SchedulingBranch,
  SchedulingResource,
  SchedulingSchedule,
  SchedulingAppointment,
  SchedulingStats,
} from 'src/app/models/scheduling.interface';
import {
  SchedulingChatRequest,
  SchedulingChatResponse,
} from 'src/app/features/chat/chat.interface';

interface APIResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class SchedulingService {
  private baseUrl = `${environment.apiAddress}/scheduling/admin`;

  private _schedulingEnabled$ = new BehaviorSubject<boolean>(false);
  schedulingEnabled$ = this._schedulingEnabled$.asObservable();

  private readonly UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  constructor(private http: HttpClient) {}

  private isValidChatbotId(id: string): boolean {
    return this.UUID_RE.test(id);
  }

  refreshSchedulingEnabled(chatbotId: string): void {
    if (!environment.schedulingEnabled) {
      this._schedulingEnabled$.next(false);
      return;
    }
    if (!this.isValidChatbotId(chatbotId)) return;
    this.getConfig(chatbotId).subscribe({
      next: (config) => this._schedulingEnabled$.next(config.is_enabled),
      error: () => this._schedulingEnabled$.next(false),
    });
  }

  setSchedulingEnabled(value: boolean): void {
    this._schedulingEnabled$.next(value);
  }

  // Config
  getConfig(chatbotId: string): Observable<SchedulingConfig> {
    return this.http
      .get<APIResponse<SchedulingConfig>>(`${this.baseUrl}/${chatbotId}/config`)
      .pipe(map((r) => r.data));
  }

  updateConfig(chatbotId: string, data: Partial<SchedulingConfig>): Observable<SchedulingConfig> {
    return this.http
      .put<APIResponse<SchedulingConfig>>(`${this.baseUrl}/${chatbotId}/config`, data)
      .pipe(map((r) => r.data));
  }

  uploadConfirmationAttachment(chatbotId: string, file: File): Observable<SchedulingConfig> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<APIResponse<SchedulingConfig>>(
        `${this.baseUrl}/${chatbotId}/config/attachment`,
        formData
      )
      .pipe(map((r) => r.data));
  }

  deleteConfirmationAttachment(chatbotId: string): Observable<SchedulingConfig> {
    return this.http
      .delete<APIResponse<SchedulingConfig>>(`${this.baseUrl}/${chatbotId}/config/attachment`)
      .pipe(map((r) => r.data));
  }

  // Services
  getServices(chatbotId: string): Observable<SchedulingServiceModel[]> {
    return this.http
      .get<APIResponse<SchedulingServiceModel[]>>(
        `${this.baseUrl}/${chatbotId}/services?active_only=false`
      )
      .pipe(map((r) => r.data));
  }

  reactivateService(
    chatbotId: string,
    serviceId: string
  ): Observable<SchedulingServiceModel> {
    return this.updateService(chatbotId, serviceId, { is_active: true });
  }

  createService(
    chatbotId: string,
    data: Partial<SchedulingServiceModel>
  ): Observable<SchedulingServiceModel> {
    return this.http
      .post<APIResponse<SchedulingServiceModel>>(`${this.baseUrl}/${chatbotId}/services`, data)
      .pipe(map((r) => r.data));
  }

  updateService(
    chatbotId: string,
    serviceId: string,
    data: Partial<SchedulingServiceModel>
  ): Observable<SchedulingServiceModel> {
    return this.http
      .put<APIResponse<SchedulingServiceModel>>(
        `${this.baseUrl}/${chatbotId}/services/${serviceId}`,
        data
      )
      .pipe(map((r) => r.data));
  }

  deleteService(chatbotId: string, serviceId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${chatbotId}/services/${serviceId}`);
  }

  // Branches
  getBranches(chatbotId: string): Observable<SchedulingBranch[]> {
    return this.http
      .get<APIResponse<SchedulingBranch[]>>(
        `${this.baseUrl}/${chatbotId}/branches?active_only=false`
      )
      .pipe(map((r) => r.data));
  }

  reactivateBranch(chatbotId: string, branchId: string): Observable<SchedulingBranch> {
    return this.updateBranch(chatbotId, branchId, { is_active: true });
  }

  createBranch(chatbotId: string, data: Partial<SchedulingBranch>): Observable<SchedulingBranch> {
    return this.http
      .post<APIResponse<SchedulingBranch>>(`${this.baseUrl}/${chatbotId}/branches`, data)
      .pipe(map((r) => r.data));
  }

  updateBranch(
    chatbotId: string,
    branchId: string,
    data: Partial<SchedulingBranch>
  ): Observable<SchedulingBranch> {
    return this.http
      .put<APIResponse<SchedulingBranch>>(`${this.baseUrl}/${chatbotId}/branches/${branchId}`, data)
      .pipe(map((r) => r.data));
  }

  deleteBranch(chatbotId: string, branchId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${chatbotId}/branches/${branchId}`);
  }

  // Resources (professionals)
  getResources(chatbotId: string, activeOnly = false): Observable<SchedulingResource[]> {
    return this.http
      .get<APIResponse<SchedulingResource[]>>(
        `${this.baseUrl}/${chatbotId}/resources?active_only=${activeOnly}`
      )
      .pipe(map((r) => r.data));
  }

  reactivateResource(chatbotId: string, resourceId: string): Observable<SchedulingResource> {
    return this.updateResource(chatbotId, resourceId, { is_active: true });
  }

  createResource(
    chatbotId: string,
    data: Partial<SchedulingResource>
  ): Observable<SchedulingResource> {
    return this.http
      .post<APIResponse<SchedulingResource>>(`${this.baseUrl}/${chatbotId}/resources`, data)
      .pipe(map((r) => r.data));
  }

  updateResource(
    chatbotId: string,
    resourceId: string,
    data: Partial<SchedulingResource>
  ): Observable<SchedulingResource> {
    return this.http
      .put<APIResponse<SchedulingResource>>(
        `${this.baseUrl}/${chatbotId}/resources/${resourceId}`,
        data
      )
      .pipe(map((r) => r.data));
  }

  deleteResource(chatbotId: string, resourceId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${chatbotId}/resources/${resourceId}`);
  }

  // Resource schedules
  getResourceSchedules(chatbotId: string, resourceId: string): Observable<SchedulingSchedule[]> {
    return this.http
      .get<APIResponse<SchedulingSchedule[]>>(
        `${this.baseUrl}/${chatbotId}/resources/${resourceId}/schedules`
      )
      .pipe(map((r) => r.data));
  }

  createResourceSchedule(
    chatbotId: string,
    resourceId: string,
    data: Partial<SchedulingSchedule>
  ): Observable<SchedulingSchedule> {
    return this.http
      .post<APIResponse<SchedulingSchedule>>(
        `${this.baseUrl}/${chatbotId}/resources/${resourceId}/schedules`,
        data
      )
      .pipe(map((r) => r.data));
  }

  // Resource associations
  assignServicesToResource(
    chatbotId: string,
    resourceId: string,
    serviceIds: string[]
  ): Observable<any> {
    return this.http
      .post<APIResponse>(`${this.baseUrl}/${chatbotId}/resources/${resourceId}/services`, {
        service_ids: serviceIds,
      })
      .pipe(map((r) => r.data));
  }

  assignBranchesToResource(
    chatbotId: string,
    resourceId: string,
    branchIds: string[]
  ): Observable<any> {
    return this.http
      .post<APIResponse>(`${this.baseUrl}/${chatbotId}/resources/${resourceId}/branches`, {
        branch_ids: branchIds,
      })
      .pipe(map((r) => r.data));
  }

  // Blocks
  createBlock(chatbotId: string, data: any): Observable<any> {
    return this.http
      .post<APIResponse>(`${this.baseUrl}/${chatbotId}/blocks`, data)
      .pipe(map((r) => r.data));
  }

  // Appointments
  getAppointments(
    chatbotId: string,
    params?: { status?: string; start_date?: string; end_date?: string }
  ): Observable<SchedulingAppointment[]> {
    let httpParams = new HttpParams();
    if (params?.status) httpParams = httpParams.set('status', params.status);
    if (params?.start_date) httpParams = httpParams.set('start_date', params.start_date);
    if (params?.end_date) httpParams = httpParams.set('end_date', params.end_date);

    return this.http
      .get<APIResponse<SchedulingAppointment[]>>(`${this.baseUrl}/${chatbotId}/appointments`, {
        params: httpParams,
      })
      .pipe(map((r) => r.data));
  }

  createAppointment(chatbotId: string, data: any): Observable<any> {
    return this.http
      .post<APIResponse>(`${this.baseUrl}/${chatbotId}/appointments`, data)
      .pipe(map((r) => r.data));
  }

  getAppointment(chatbotId: string, appointmentId: string): Observable<SchedulingAppointment> {
    return this.http
      .get<APIResponse<SchedulingAppointment>>(
        `${this.baseUrl}/${chatbotId}/appointments/${appointmentId}`
      )
      .pipe(map((r) => r.data));
  }

  cancelAppointment(chatbotId: string, appointmentId: string): Observable<any> {
    return this.http
      .put<APIResponse>(`${this.baseUrl}/${chatbotId}/appointments/${appointmentId}/cancel`, {})
      .pipe(map((r) => r.data));
  }

  completeAppointment(chatbotId: string, appointmentId: string): Observable<any> {
    return this.http
      .put<APIResponse>(`${this.baseUrl}/${chatbotId}/appointments/${appointmentId}/complete`, {})
      .pipe(map((r) => r.data));
  }

  markNoShow(chatbotId: string, appointmentId: string): Observable<any> {
    return this.http
      .put<APIResponse>(`${this.baseUrl}/${chatbotId}/appointments/${appointmentId}/no-show`, {})
      .pipe(map((r) => r.data));
  }

  rescheduleAppointment(
    chatbotId: string,
    appointmentId: string,
    payload: {
      resource_id: string;
      start_datetime: string;
      service_id?: string;
      branch_id?: string;
      notes?: string;
    }
  ): Observable<{
    id: string;
    start_datetime: string;
    end_datetime: string;
    status: string;
    origin_appointment_id: string | null;
  }> {
    return this.http
      .put<
        APIResponse<{
          id: string;
          start_datetime: string;
          end_datetime: string;
          status: string;
          origin_appointment_id: string | null;
        }>
      >(`${this.baseUrl}/${chatbotId}/appointments/${appointmentId}/reschedule`, payload, {
        withCredentials: true,
      })
      .pipe(map((r) => r.data));
  }

  // Setup
  setupScheduling(chatbotId: string, data: any): Observable<SchedulingConfig> {
    return this.http
      .post<APIResponse<SchedulingConfig>>(`${this.baseUrl}/${chatbotId}/setup`, data)
      .pipe(map((r) => r.data));
  }

  // Delete schedule
  deleteSchedule(chatbotId: string, scheduleId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${chatbotId}/schedules/${scheduleId}`);
  }

  // Stats
  getStats(chatbotId: string): Observable<SchedulingStats> {
    return this.http
      .get<APIResponse<SchedulingStats>>(`${this.baseUrl}/${chatbotId}/stats`)
      .pipe(map((r) => r.data));
  }

  // Quota / limits
  getLimitsStatus(chatbotId: string): Observable<{
    limit_reached: boolean;
    current: number;
    max: number;
    reset_date: string;
    professionals: { current: number; max: number; limit_reached: boolean };
  }> {
    return this.http
      .get<
        APIResponse<{
          limit_reached: boolean;
          current: number;
          max: number;
          reset_date: string;
          professionals: { current: number; max: number; limit_reached: boolean };
        }>
      >(`${this.baseUrl}/${chatbotId}/limits-status`)
      .pipe(map((r) => r.data));
  }

  // Chat (public endpoint, not admin)
  sendSchedulingMessage(
    chatbotToken: string,
    data: SchedulingChatRequest
  ): Observable<SchedulingChatResponse> {
    return this.http.post<SchedulingChatResponse>(
      `${environment.apiAddress}/scheduling/chat/${chatbotToken}`,
      data
    );
  }
}
